import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Lock, Mail, User, KeyRound, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import MFACodeForm from './login/MFACodeForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [isLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    mfa_code: '',
    reset_code: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    new_password: false,
    confirm_password: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password on registration
    if (!isLogin) {
      if (formData.password.length < 14) {
        toast.error('Password must be at least 14 characters');
        return;
      }
      if (formData.password.length > 64) {
        toast.error('Password must not exceed 64 characters');
        return;
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { 
            email: formData.email, 
            password: formData.password,
            mfa_code: mfaRequired ? formData.mfa_code : undefined
          }
        : { email: formData.email, password: formData.password, full_name: formData.full_name };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaRequired(true);
        toast.info('Please enter your 2FA code');
        setLoading(false);
        return;
      }

      // Privileged role without MFA — force enrollment on /security
      if (response.data.mfa_enrollment_required) {
        localStorage.setItem('mfa_enrollment_required', 'true');
        onLogin(response.data.token, response.data.user, null);
        toast.info(response.data.message || 'MFA setup required to continue.', { duration: 6000 });
        setLoading(false);
        return;
      }

      localStorage.removeItem('mfa_enrollment_required');
      onLogin(response.data.token, response.data.user, response.data.refresh_token);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Authentication failed';
      if (status === 423) {
        toast.error('Account locked. Please reset your password.', { duration: 6000 });
        setForgotPassword(true);
      } else if (status === 403 && typeof detail === 'object' && detail?.code === 'MFA_DEADLINE_PASSED') {
        toast.error(detail.message || 'MFA enforcement deadline has passed. Contact your super admin.', { duration: 12000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Authentication failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/password/forgot`, { email: formData.email });
      setResetCodeSent(true);
      toast.success('Reset code sent! Check your email.');
      // In dev mode, the API returns the code for testing convenience
      if (response.data.reset_code) {
        toast.info(`Test mode: Reset code is ${response.data.reset_code}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.new_password.length < 14) {
      toast.error('Password must be at least 14 characters');
      return;
    }
    
    if (formData.new_password.length > 64) {
      toast.error('Password must not exceed 64 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password/reset`, {
        email: formData.email,
        reset_code: formData.reset_code,
        new_password: formData.new_password,
        mfa_code: formData.mfa_code || undefined,
      });
      toast.success('Password reset successfully! You can now login.');
      setForgotPassword(false);
      setResetCodeSent(false);
      setFormData({ ...formData, reset_code: '', new_password: '', confirm_password: '', mfa_code: '' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend signals privileged users must also provide a TOTP code.
      if (detail && typeof detail === 'object' && detail.code === 'MFA_REQUIRED_FOR_RESET') {
        toast.error(detail.message || 'Enter your 6-digit MFA code to complete the reset.', { duration: 6000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setForgotPassword(false);
    setResetCodeSent(false);
    setFormData({ ...formData, mfa_code: '', reset_code: '', new_password: '', confirm_password: '' });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1619021717707-d0bc2b9d1493?w=1920&q=80)'
        }}
      />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/20" />
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-md p-8">
          {/* Logo & Title */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-md flex items-center justify-center mb-4 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="app-title">
              My Shield
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              {mfaRequired ? 'Two-Factor Authentication' : 
               forgotPassword ? 'Reset Password' : 
               'Compliance Screening Platform'}
            </p>
          </div>

          {/* Forgot Password Flow */}
          {forgotPassword ? (
            resetCodeSent ? (
              /* Reset Password Form */
              <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
                <div className="text-center mb-4">
                  <Lock className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600">
                    Enter the reset code sent to your email and create a new password
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="reset_code" className="text-slate-700 text-sm font-medium">
                    Reset Code
                  </Label>
                  <div className="relative mt-1.5">
                    <KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="reset_code"
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={formData.reset_code}
                      onChange={(e) => setFormData({ ...formData, reset_code: e.target.value.replace(/\D/g, '') })}
                      className="pl-10 font-mono text-center text-lg tracking-widest"
                      required
                      data-testid="input-reset-code"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="new_password" className="text-slate-700 text-sm font-medium">
                    New Password
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="new_password"
                      type={showPassword.new_password ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                      data-testid="input-new-password"
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => ({...p, new_password: !p.new_password}))} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" data-testid="toggle-new-password">
                      {showPassword.new_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Minimum 14 characters, maximum 64. Cannot reuse last 5 passwords.</p>
                </div>
                
                <div>
                  <Label htmlFor="confirm_password" className="text-slate-700 text-sm font-medium">
                    Confirm Password
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirm_password"
                      type={showPassword.confirm_password ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                      data-testid="input-confirm-password"
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => ({...p, confirm_password: !p.confirm_password}))} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" data-testid="toggle-confirm-password">
                      {showPassword.confirm_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="reset_mfa_code" className="text-slate-700 text-sm font-medium">
                    MFA Code <span className="text-slate-400 font-normal">(required for admin accounts)</span>
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="reset_mfa_code"
                      type="text"
                      maxLength={8}
                      placeholder="Authenticator or backup code"
                      value={formData.mfa_code || ''}
                      onChange={(e) => setFormData({ ...formData, mfa_code: e.target.value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase() })}
                      className="pl-10 font-mono text-center tracking-widest"
                      data-testid="input-reset-mfa-code"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">super_admin and org_admin accounts need a 6-digit code from your authenticator app (or an 8-char backup code).</p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-6"
                  disabled={loading || formData.reset_code.length !== 6}
                  data-testid="submit-reset-password"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToLogin}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>
            ) : (
              /* Request Reset Code Form */
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
                <div className="text-center mb-4">
                  <Mail className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600">
                    Enter your email address and we&apos;ll send you a reset code
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="reset_email" className="text-slate-700 text-sm font-medium">
                    Email Address
                  </Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="reset_email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="input-reset-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-6"
                  disabled={loading}
                  data-testid="submit-forgot-password"
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToLogin}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>
            )
          ) : mfaRequired ? (
            <MFACodeForm
              mfaCode={formData.mfa_code}
              onMfaCodeChange={(v) => setFormData({ ...formData, mfa_code: v })}
              onSubmit={handleSubmit}
              onBack={handleBackToLogin}
              loading={loading}
            />
          ) : (
            /* Regular Login/Register Form */
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && (
                <div>
                  <Label htmlFor="full_name" className="text-slate-700 text-sm font-medium">
                    Full Name
                  </Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      id="full_name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="pl-10"
                      required={!isLogin}
                      data-testid="input-full-name"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="compliance@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword.password ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10"
                    required
                    data-testid="input-password"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => ({...p, password: !p.password}))} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" data-testid="toggle-password">
                    {showPassword.password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-slate-500 mt-1">Minimum 14 characters, maximum 64. Unicode accepted.</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-6"
                disabled={loading}
                data-testid="submit-auth-button"
              >
                {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
              
              {/* Forgot Password Link */}
              {isLogin && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setForgotPassword(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </form>
          )}

          {/* Note - Public registration is disabled */}
          {!mfaRequired && !forgotPassword && (
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Contact your administrator to request an account
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-white/80">
          Secure compliance screening powered by OFAC & OpenSanctions
        </div>
      </div>
    </div>
  );
}